import React, { createRef, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { throttle } from 'underscore';

import Description from './description/description';
import Documents from './documents/documents';
import Surveys from './surveys/surveys';
import Products from './products/products';
import Questions from './questions';
import Quizzes from './quizzes';
import SimilarSessions from './similar-sessions/similar-sessions';
import Slideshows from './slideshows';
import Speakers from './speakers/speakers';
import Video, { VideoWrapper } from './video';
import {
	EBroadcastTypes, ENavbarTypes,
	EPreviewTypes,
	FeatureFlagsEnum,
	ModuleGroups,
	NavMainItems,
	PageModuleGroupModules,
	PageModuleType,
	Session,
	SessionTabsLayoutTypes,
	SessionTypesEnum,
	TranslateString,
} from '../../../../../types/working-model';
import { setActiveSessionGroupModule, updateSessionStyling } from '../../../../../store/actions/admin/create-event/session';
import BlankSection from '../../homepage/editor/blank-section';
import Sticky from '../../../../general-ui/sticky/sticky';
import { useTypedSelector } from '../../../../../store/reducers/use-typed-selector';
import Icon, { COLORS, ICONS } from '../../../../general-ui/icon';
import RenderModules from './render-modules';
import useTransformGroupModules from '../../../../../utils/use-transform-group-modules';
import { EditorSizes } from '../../../../../types/template-layouts';
import SessionHeader from '../../../../live-event/session/session-modules/header/header';
import { PostRegisterSideBar } from '../../homepage/post-register-preview/main-preview';
import { GetDefaultStylingOverrides, GetDefaultMainNavItems } from '../../../../../store/utils/create-event';
import { resetScrollState } from '../../../../../store/actions/admin/event-panel';
import SessionEditorHeader from './session-editor-header';
import SessionPreviewModal from '../../../../general-ui/session-preview/session-preview';
import EditSectionColorsModal, { EContentColorOverrideNames } from '../../../../general-ui/edit-section-colors/edit-section-colors-modal';
import { getSessionStylingOverrides } from '../../../../live-event/utils';
import Footer from '../../../../live-event/marketing-page/footer/footer';
import FiresideHostControls from "../../../../live-event/session/fireside/session-host";
import useTimestampStatus from '../../../../../utils/use-timestamp-status';
import PreviewEventChatMain from '../../../../live-event/event-chat/preview-event-chat-main';
import SessionStream from '../../../../live-event/session/session-stream/session-stream';
import { OptionalComponent } from '../../../../../utils/optional-component';
import BroadcastOptionsModal from '../../homepage/navigation/broadcast-options-modal';
import { Tooltip } from '../../../../general-ui/tooltip/tooltip';
import { getTemplateClassName, isRtlLanguage } from '../../../../../utils/utils';
import ReadOnlyIndicator from '../../shared/read-only-indicator';
import { useReadonlyMode } from '../../shared/use-readonly-mode';
const LiveChat = React.lazy(() => import('../../../../live-event/session/live-chat/live-chat'));
import FeedbackSurveyAlert from '../../../../general-ui/user-survey-modal/user-survey-alert';

import './editor-main.scss';
import './session-main-below-the-fold.scss';

import { LanguageDropdown } from '../../../../general-ui/language-dropdown/language-dropdown';
import CookieBannerStickyWrapper from '../../../../general-ui/cookie-banner/cookie-banner-sticky-wrapper';
import CookieBanner from '../../../../general-ui/cookie-banner/cookie-banner';
import { useBelowTheFoldScroll } from '../../../../../hooks/below-the-fold-scroll';
import { useIsNewNavigation } from '../../../../../hooks/navigation.hooks';
import { useIsSingleSessionWithoutHome, useActiveModulesInGroup, useIsBelowTheFold, useIsAboveTheFold } from '../../../../../hooks/session.hooks';
import MarketingNavV2 from '../../../../live-event/marketing-page/navigation-v2/marketing-nav-v2';
import GroupedNavButtonsEditor from './grouped-nav-buttons-editor';
import { updateAllEventSessionsLayoutTabs } from '../../../../../store/actions/admin/create-event';

// for testing only:
// import VideoPlayer from '../../../../video-player/video-player';
// import Stream from '../../../../video-player/stream';

interface ModuleMapType {
	[key: number]: any;
}

function Dummy(): JSX.Element {
	return <div></div>;
}

export const EngageCount: React.FC<{ group: PageModuleGroupModules, session: Session }> = ({ group, session }) => {

	const moduleGroupName = group?.name as TranslateString;
	const _name = moduleGroupName?.base ?? moduleGroupName;
	if(_name !== ModuleGroups.Engage) return <></>;
 
	const activeModules = useActiveModulesInGroup(session, group);

	let totalModules = 0;
	activeModules.forEach((module) => {
		totalModules += module?.modules?.length || 1;
	});

	if (!activeModules.length) return <></>;

	return (
		<div className='engage-count'>{totalModules}</div>
	);
};

export const VideoGutter: React.FC = () => <div className="video-gutter"></div>;

//Maps the page module type to the function for the component.
export const ModuleMap: ModuleMapType = {
	[PageModuleType.main_event_banner]: Dummy,
	[PageModuleType.speakers]: Speakers,
	[PageModuleType.agenda]: Dummy,
	[PageModuleType.about]: Dummy,
	[PageModuleType.videos]: Dummy,
	[PageModuleType.callout]: Dummy,
	[PageModuleType.sponsors]: Dummy,
	[PageModuleType.broadcast]: Dummy,//dead module type, but don't remove from the enumerator or things will break
	[PageModuleType.questions]: Questions,
	[PageModuleType.quizzes]: Quizzes,
	[PageModuleType.survey]: Surveys,
	[PageModuleType.products]: Products,
	[PageModuleType.slideshows]: Slideshows,
	[PageModuleType.documents]: Documents,
	[PageModuleType.similar_sessions]: SimilarSessions,
	[PageModuleType.feed]: Dummy,
	[PageModuleType.embed_widget]: BlankSection,
	[PageModuleType.description]: Description,
	[PageModuleType.blank]: BlankSection
};

export const ModuleMapNavButtons: ModuleMapType = {
	[PageModuleType.main_event_banner]: "",
	[PageModuleType.speakers]: "Speakers",
	[PageModuleType.agenda]: "",
	[PageModuleType.about]: "",
	[PageModuleType.videos]: "",
	[PageModuleType.callout]: "",
	[PageModuleType.sponsors]: "",
	[PageModuleType.broadcast]: "",
	[PageModuleType.questions]: "Questions",
	[PageModuleType.quizzes]: "Quizzes",
	[PageModuleType.survey]: "Surveys",
	[PageModuleType.products]: "Products",
	[PageModuleType.slideshows]: "Slideshows",
	[PageModuleType.documents]: "Documents",
	[PageModuleType.similar_sessions]: "Similar Sessions",
	[PageModuleType.feed]: "Feed",
	[PageModuleType.embed_widget]: "Embed Widget",
	[PageModuleType.description]: "Description",
	[PageModuleType.blank]: "Blank"
};

const Editor: React.FC = () => {
	const {
		CreateSessionReducer: {
			workingSession,
			selectedPreviewGroupUuid,
			sessionLoading
		},
		EventPanelReducer: {
			panelOpen,
			sessionEditorGroup,
		},
		CreateEventReducer: {
			workingEvent,
			editorSize,
			previewMode
		},
	} = useTypedSelector(state => state);
	const featureFlags = useTypedSelector(state => state.FeatureFlagsReducer.featureFlags);
	const sessionDetailsV2FeatureFlagOn = featureFlags[FeatureFlagsEnum.session_details_v2];

	const [openStyleModal, setOpenStyleModal] = useState(false);
	const [optionsModalOpen, setOptionsModalOpen] = useState(false);
	const [chatTopPosition, setChatTopPosition] = useState(0);
	const [cookieBannerTopPosition, setCookieBannerTopPosition] = useState(0);
	const [interacted, setInteracted] = useState(false);
	const [cookieBannerHeight, setCookieBannerHeight] = useState(0);
	const [editorSizeChangedAsNum, setEditorSizeChangedAsNum] = useState(0);
	const [isScrolledPastTheFold, setIsScrolledPastTheFold] = useState(false);
	const [moduleGroupsRefs, setModuleGroupsRefs] = useState<React.RefObject<HTMLParagraphElement>[]>([]);
	const [autoScroll, setAutoScroll] = useState(false);
	const dispatch = useDispatch();
	const isReadOnly = useReadonlyMode(true);
	const { language }: any = useParams();

	const timestampStatus = useTimestampStatus(workingSession);
	const sessionHeaderRef = useRef<HTMLDivElement | null>(null);
	
	const isSessionDetailsBelowTheFoldV2 = useIsBelowTheFold(workingSession);
	const isSessionDetailsAboveTheFoldV2 = useIsAboveTheFold(workingSession);

	
	useEffect(() => {
		const _moduleGroupsRefs = workingSession?.module_grouping?.map(() => createRef<HTMLParagraphElement>()) || [];
		setModuleGroupsRefs(_moduleGroupsRefs);
	}, [workingSession?.module_grouping]);


	const {
		backgroundColor,
		backgroundImg
	} = getSessionStylingOverrides(workingSession?.layout?.styling_overrides);

	const template = getTemplateClassName(workingEvent?.template.name);

	const displaySessionLanguageDropdown = (workingSession?.languages ?? []).length > 1 && workingEvent?.settings?.display_session_language_dropdown;

	useTransformGroupModules();

	const sessionWrapperRef = useRef<HTMLDivElement>(null);
	const editorMainContainerRef = useRef<HTMLDivElement>(null);
	const scrollContainer = useRef<HTMLDivElement | null>(null);
	const belowTheFoldContainer = useRef<HTMLDivElement | null>(null);


	const templateFileName = workingEvent?.template?.name?.toLocaleLowerCase().replace(/\s/g, '') ?? '';

	useEffect(() => {
		// async import live event template styling
		async function loadTemplate() {
			if (templateFileName) {
				await import(`../../../../../scss/live-event/${templateFileName}.scss`);
			}
		}
		loadTemplate();
	}, [templateFileName]);

	function scrollToEditor() {
		// The ref for this scroll behavior is the window itself, not any specific module
		// Commenting for now until we figure out a scroll behavior with tabs
		// if (sessionWrapperRef.current) {
		// 	sessionWrapperRef.current.scrollIntoView({
		// 		behavior: 'smooth',
		// 		block: 'start',
		// 	});
		// }
	}

	const handleScroll = useBelowTheFoldScroll(
		autoScroll,
		true, // Is Preview
		isScrolledPastTheFold,
		moduleGroupsRefs,
		scrollContainer,
		selectedPreviewGroupUuid,
		workingSession?.module_grouping,
		setIsScrolledPastTheFold,
		null,
		setActiveSessionGroupModule

	); 

	
	useEffect(() => {
		if (isSessionDetailsBelowTheFoldV2 && sessionDetailsV2FeatureFlagOn) {
			const editorRef = editorMainContainerRef?.current;

			if (scrollContainer.current) {
				editorRef?.addEventListener('scroll', handleScroll);
				window.addEventListener('resize', handleScroll);
			}

			return () => {
				editorRef?.removeEventListener('scroll', handleScroll);
				window.removeEventListener('resize', handleScroll);
			};
		}
	}, [autoScroll, dispatch, handleScroll, isScrolledPastTheFold, isSessionDetailsBelowTheFoldV2, moduleGroupsRefs, selectedPreviewGroupUuid, sessionDetailsV2FeatureFlagOn, workingSession?.module_grouping]);

	// When we autoscroll, we don't want the tabs to auto switch when starting in a different section than the selected section
	useEffect(() => {
		setTimeout(()=>{
			if (autoScroll) {	
				setAutoScroll(false);
			}
		},1000);
	}, [autoScroll]);
	
	// when we change the editor size, we need to force a re-render of the cookie banner
	// but we must wait a second because it takes some time for the editor to resize
	useEffect(() => {
		setTimeout(() => {
			setEditorSizeChangedAsNum(editorSize === EditorSizes.desktop
				? 0
				: editorSize === EditorSizes.tablet
					? 1
					: 2
			);
		}, 1000);
	}, [editorSize]);

	useEffect(() => {
		if (sessionEditorGroup) {
			dispatch(setActiveSessionGroupModule(sessionEditorGroup));
			setTimeout(() => scrollToEditor(), 100);
			setTimeout(() => dispatch(resetScrollState()), 1500);
		}
	}, [dispatch, sessionEditorGroup]);

	useEffect(() => {
		if (interacted) return;

		const hasInteracted = () => {
			setInteracted(true);
		};

		window.addEventListener('click', hasInteracted);
		window.addEventListener('touchstart', hasInteracted);

		return () => {
			window.removeEventListener('click', hasInteracted);
			window.removeEventListener('touchstart', hasInteracted);
		};
	}, [interacted]);

	const layout: SessionTabsLayoutTypes = workingSession?.layout?.tabs?.layout || SessionTabsLayoutTypes.Horizontal;

	const renderModulesByGroup = () => {
		const uuid = selectedPreviewGroupUuid || workingSession?.module_grouping?.[0].uuid;
		const breakoutRoomsTab = workingSession?.module_grouping?.find(module_group => module_group.uuid === uuid && (module_group.type === "breakout"));
		if (!uuid) return <></>;
		return <RenderModules groupUuid={uuid} isBreakoutRooms={breakoutRoomsTab ? true : false} breakoutsOff={breakoutRoomsTab ? !breakoutRoomsTab.is_on : false} template={template} />;
	};

	const renderAllModules = () => {
		return (
			<>
				{workingSession?.module_grouping?.map(group => (
					<RenderModules
						key={group.uuid}
						groupUuid={group.uuid}
						template={template}
						isBreakoutRooms={group.type === "breakout"}
					/>))}
			</>
		);
	};

	const renderAllModulesBelowTheFold = () => {
		return (
			<>
				{workingSession?.module_grouping?.map((group, i: number) => {
					const moduleGroupName = group?.name as TranslateString;
					const _name = moduleGroupName?.base ?? moduleGroupName;

					const activeModules = useActiveModulesInGroup(workingSession, group);
					// if (!activeModules.length) return <></>;

					return (
						<>
							<p className={"module-title"} id={`${_name}-container`} ref={moduleGroupsRefs[i]} >{moduleGroupName.base}</p>
							<RenderModules
								key={group.uuid}
								groupUuid={group.uuid}
								template={template}
								isBreakoutRooms={group.type === "breakout"}
							/>
						</>
					);
				})}
			</>
		);
	};

	const handleFinishStyling = useCallback((colorKey: string) => {
		if (!workingSession) return;

		const workingBackground = {
			...(workingSession?.layout?.styling_overrides?.background ?? GetDefaultStylingOverrides()?.background)
		};

		if (!colorKey) {
			// if restore default, reset background and save image
			workingBackground.image = {
				src: workingBackground?.image?.src ?? '',
				is_on: false
			};
			workingBackground.color = '';

		} else if (colorKey.startsWith('https://')) {
			// using a background image
			workingBackground.image = {
				src: colorKey,
				is_on: true
			};
			workingBackground.color = '';

		} else {
			// using a background color
			workingBackground.color = `${colorKey}-background`;
		}

		const updatedSessionStyling = {
			...workingSession?.layout.styling_overrides,
			background: workingBackground
		};

		dispatch(updateSessionStyling(updatedSessionStyling));
	}, [dispatch, workingSession]);

	const handleCancelStyling = useCallback(() => {
		setOpenStyleModal(false);
	}, []);

	const renderContent = () => {
		//Vertical layout is only available on desktop, switch to horizontal view if vertical is chosen but viewed on mobile or tablet
		let _layout = (editorSize !== "desktop" && layout === SessionTabsLayoutTypes.Vertical)
			? SessionTabsLayoutTypes.Horizontal
			: layout;
		
		
		const isNewLayout = isSessionDetailsBelowTheFoldV2 || isSessionDetailsAboveTheFoldV2;
		const isSessionDetailsV2FF = featureFlags[FeatureFlagsEnum.session_details_v2];

		const revertLayoutToDefaultCheck = !isSessionDetailsV2FF && isNewLayout;

		if (revertLayoutToDefaultCheck) {
			_layout = SessionTabsLayoutTypes.NoTabs;
		}

		switch (_layout) {
			case SessionTabsLayoutTypes.Horizontal:
				return (
					<div
						className={classNames("page-container-horizontal", backgroundColor, { ['display-language-dropdown']: displaySessionLanguageDropdown })}
						style={backgroundImg}
					>
						<OptionalComponent display={displaySessionLanguageDropdown && editorSize !== EditorSizes.mobile}>
							<div className="language-dropdown-container">
								<LanguageDropdown languages={[]} currentLanguageCode={language} useISOLangCodes isNoScroll={false} animateDropdown />
							</div>
						</OptionalComponent>
						<div className="container-styling">
							<button className="button round" onClick={() => setOpenStyleModal(true)}>
								<Icon
									name={ICONS.PAINTBRUSH}
									color={COLORS.WHITE}
									size={12}
									backgroundColor={COLORS.DARK_GREY}
								/>
							</button>
						</div>
						<div className="page-container-body">
							{renderModulesByGroup()}
						</div>
						{workingSession?.enable_feedback_survey &&
							<FeedbackSurveyAlert rating={0} show layout={layout} />}
						<div className="page-container-horizontal-tabs-container read-only-exception">
							<ul className="page-container-horizontal-tabs tab-navigation">
								<GroupedNavButtonsEditor
									moduleGroupsRefs={moduleGroupsRefs}
									autoScroll={autoScroll}
									setAutoScroll={setAutoScroll}
								/>
							</ul>
						</div>
					</div>
				);
			case SessionTabsLayoutTypes.NoTabs:
				return (
					<div
						className={classNames(
							"editor-modules page-container-body",
							{
								"page-container-horizontal": editorSize === 'mobile',
								["display-language-dropdown"]: displaySessionLanguageDropdown
							})
						}
						style={backgroundImg}
					>
						<OptionalComponent display={displaySessionLanguageDropdown && editorSize !== EditorSizes.mobile}>
							<div className="language-dropdown-container no-tabs">
								<LanguageDropdown languages={[]} currentLanguageCode={language} useISOLangCodes isNoScroll={false} animateDropdown />
							</div>
						</OptionalComponent>
						<div className="container-styling">
							<button className="button round" onClick={() => setOpenStyleModal(true)}>
								<Icon
									name={ICONS.PAINTBRUSH}
									color={COLORS.WHITE}
									size={12}
									backgroundColor={COLORS.DARK_GREY}
								/>
							</button>
						</div>
						{workingSession?.enable_feedback_survey &&
							<FeedbackSurveyAlert rating={0} show layout={layout} />}
						{renderAllModules()}
					</div>
				);
			
			case SessionTabsLayoutTypes.BelowTheFold:
				return (
					<>
						<OptionalComponent display={isScrolledPastTheFold}>
							<div className="page-container-below-the-fold-tabs-container read-only-exception scrolled-past-the-fold-active"
								ref={belowTheFoldContainer}
							>
								<ul className="page-container-below-the-fold-tabs tab-navigation">
									<GroupedNavButtonsEditor
										moduleGroupsRefs={moduleGroupsRefs}
										autoScroll={autoScroll}
										setAutoScroll={setAutoScroll}
									/>
								</ul>
							</div>
						</OptionalComponent>
						<div className='below-the-fold-with-gutter'>
							<div
								className={classNames("page-container-below-the-fold", backgroundColor, {
									['display-language-dropdown']: displaySessionLanguageDropdown
								})}
								style={backgroundImg}
							>
								<div className="container-styling">
									<button className="button round" onClick={() => setOpenStyleModal(true)}>
										<Icon
											name={ICONS.PAINTBRUSH}
											color={COLORS.WHITE}
											size={12}
											backgroundColor={COLORS.DARK_GREY}
										/>
									</button>
								</div>
								<div className="page-container-body">
									{renderAllModulesBelowTheFold()}
								</div>
								{workingSession?.enable_feedback_survey &&
									<FeedbackSurveyAlert rating={0} show layout={layout} />}
								
								<div className={classNames("page-container-below-the-fold-tabs-container read-only-exception",
									{ ['scrolled-past-the-fold']: isScrolledPastTheFold })}
									ref={belowTheFoldContainer}
								>
									<ul className="page-container-below-the-fold-tabs tab-navigation">
										<GroupedNavButtonsEditor
											moduleGroupsRefs={moduleGroupsRefs}
											autoScroll={autoScroll}
											setAutoScroll={setAutoScroll}
										/>
									</ul>
								</div>
							</div>
							{isSessionDetailsBelowTheFoldV2 ? <VideoGutter /> : <></>}
						</div>
					</>
				);
			
			case SessionTabsLayoutTypes.AboveTheFold:
				return (
					<></>
				);

			default:
				return (
					<div
						className={classNames("page-container-vertical", backgroundColor, { ["display-language-dropdown"]: displaySessionLanguageDropdown })}
						style={backgroundImg}
					>
						<OptionalComponent display={displaySessionLanguageDropdown && editorSize !== EditorSizes.mobile}>
							<div className="language-dropdown-container vertical-tabs">
								<LanguageDropdown languages={[]} currentLanguageCode={language} useISOLangCodes isNoScroll={false} animateDropdown />
							</div>
						</OptionalComponent>
						<div className="container-styling">
							<button className="button round" onClick={() => setOpenStyleModal(true)}>
								<Icon
									name={ICONS.PAINTBRUSH}
									color={COLORS.WHITE}
									size={12}
									backgroundColor={COLORS.DARK_GREY}
								/>
							</button>
						</div>
						<div>
							<Sticky
								top={workingSession && workingSession.languages?.length > 1 ? "70px" : '0px'}
								renderStickyElements={() => (
									<div className="editor-navigation">
										<ul className="tab-navigation" >
											<GroupedNavButtonsEditor
												moduleGroupsRefs={moduleGroupsRefs}
												autoScroll={autoScroll}
												setAutoScroll={setAutoScroll}
											/>
										</ul>
									</div>
								)}
							/>
						</div>
						<div className="editor-modules page-container-body">
							{workingSession?.enable_feedback_survey &&
								<FeedbackSurveyAlert rating={0} show layout={layout} />}
							{renderModulesByGroup()}
						</div>
					</div>
				);
		}
	};
	const isDesktop = editorSize === EditorSizes.desktop;
	const isSingleSession = workingEvent?.sessions?.length === 1;
	const hasHomepage = workingEvent?.homepage;
	const activeCustomPages = workingEvent?.custom_pages?.filter(page => page.is_on);
	const shouldDisplayFooter = workingEvent?.settings?.display_session_footer ?? false;
	const isFireside = workingSession?.session_type === SessionTypesEnum.fireside;
	const isBreakout = workingSession?.session_type === SessionTypesEnum.breakoutRooms;
	const isIFrameBroadcast = workingSession?.session_type === SessionTypesEnum.broadcast && workingSession.broadcast_type === EBroadcastTypes.embed;

	const shouldUseMobileChat = !isBreakout
		&& (editorSize === EditorSizes.mobile
			|| (isFireside && editorSize !== EditorSizes.desktop)
			|| (isIFrameBroadcast && editorSize !== EditorSizes.desktop));

	// because of absolute positioning of chat bubble it won't scroll within the editor
	// we need to dynamically set the scroll top position to keep it moving with scroll
	const eventChatEnabled = workingEvent?.social_settings?.messaging;
	const throttleScroll = throttle((event: any) => {
		if (eventChatEnabled) {
			setChatTopPosition(event?.target?.scrollTop);
		}
		setCookieBannerTopPosition(event?.target?.scrollTop);
	}, 1000);
	const forceRtl = isRtlLanguage(language);
	
	const isNewNavigation = useIsNewNavigation();
	const isSingleSessionWithoutHome = useIsSingleSessionWithoutHome();
	const navbarType = workingEvent?.settings?.event_navbar?.navbarType;
	const isTopNavbar = navbarType === ENavbarTypes.Horizontal;

	return workingSession && workingEvent ? (
		<>
			<div className={classNames("session-editor editor-main-border", { "force-rtl": forceRtl })}>
				<SessionEditorHeader session={workingSession} />
				<div
					className={classNames(
						"editor editor-main-container session",
						editorSize,
						templateFileName,
						{
							'panel-open': !!panelOpen,
							loading: sessionLoading,
							'read-only': isReadOnly,
							'has-homepage': hasHomepage,
							'new-top-navigation': isDesktop && (isTopNavbar && isNewNavigation || isSingleSessionWithoutHome && isNewNavigation),
						})}
					onScroll={throttleScroll}
					ref={editorMainContainerRef}
				>
					<OptionalComponent display={isDesktop && isNewNavigation && isSingleSessionWithoutHome}>
						<MarketingNavV2 template={template} previewHide={false} />
					</OptionalComponent>
					
					{isDesktop && hasHomepage && (
						<PostRegisterSideBar
							template={template}
							eventMainNavItems={workingEvent?.homepage?.event_main_nav_items || GetDefaultMainNavItems()} activeMenuItem={isSingleSession ? NavMainItems.Session : NavMainItems.Sessions}
							logo={workingEvent.homepage?.header.logo}
							disableProfileBubble
							customPages={activeCustomPages}
						/>)
					}
					<div
						className={classNames("editor-wrapper-right", {
							'fireside-session': isFireside,
							[timestampStatus]: timestampStatus,
						})}
					>
						<div className='editor-session-header-and-stream' ref={scrollContainer}>
							{/* don't show if breakout room, because the header is shown inside main-event-banner-breakout.tsx */}
							{/* because we need the header to be overlaid over the whole banner */}
							{workingSession.session_type !== SessionTypesEnum.breakoutRooms ? (
								<SessionHeader
									session={workingSession}
									eventBundle={workingEvent}
									isEditor
									editorSize={editorSize}
									hideNavItems={isDesktop}
									ref={sessionHeaderRef}
								/>
							) : null}
							{/* for testing only: */}
							{/* <div>
					<VideoPlayer streamUrl={workingSession?.ivs_channel?.channel?.playbackUrl} />
					</div>
					<div>
						<Stream />
					</div> */}

							{workingSession.session_type === SessionTypesEnum.fireside ? (
								<>
									<VideoWrapper template={template} styleOverrides={{}}>
										<div className='fireside-tooltip'>
											<Tooltip tooltip="Fireside video content is available on session page" position='left'>
												<Icon name={ICONS.PRIMARY_TOOLTIP} color={COLORS.CYAN} size={25} />
											</Tooltip>
										</div>
										<SessionStream
											languageProp={language}
											session={workingSession}
											eventBundle={workingEvent}
											editorSize={editorSize}
											isAdmin
											isEditor
											sessionHeaderRef={sessionHeaderRef}
											playback={featureFlags[FeatureFlagsEnum.firesides_broadcasting] ? workingEvent.playbackUrls?.[`${workingSession.uuid}-${language}`] : undefined}
										/>
									</VideoWrapper>
									<div className="video-actions fireside">
										<button className="button round" onClick={() => setOpenStyleModal(true)}>
											<Icon
												name={ICONS.PAINTBRUSH}
												color={COLORS.WHITE}
												size={12}
												backgroundColor={COLORS.DARK_GREY}
											/>
										</button>
										<button className="button round" onClick={() => setOptionsModalOpen(true)}>
											<Icon
												name={ICONS.EDIT}
												color={COLORS.WHITE}
												size={12}
												backgroundColor={COLORS.DARK_GREY}
											/>
										</button>
									</div>
								</>
							) : (
								<Video
									session_type={workingSession.session_type}
									editorSize={editorSize}
									language={language}
									interacted={interacted}
									sessionHeaderRef={sessionHeaderRef}
								/>
							)}

							{shouldUseMobileChat && (
								<Suspense fallback="">
									<LiveChat
										session={workingSession}
										loading={false}
										language={language}
										registrationOn={!!workingEvent?.registration_on}
										blProfileUser={null}
										template={template}
										forceOverlay={false}
										mobileOnly={true}
										isEditor={true}
										chatClosed={false}
										onChatClose={() => ({})}
										eventBundle={workingEvent}
										isAdmin={true}
									/>
								</Suspense>
							)}
						</div>
						<div
							ref={sessionWrapperRef}
							className={classNames("editor-wrapper session-editor-wrapper", template, {
								'breakout-session': workingSession?.session_type === SessionTypesEnum.breakoutRooms,
							})}
						>
							<FiresideHostControls session={workingSession} template={template} admin />
							{renderContent()}
							<SessionPreviewModal open={previewMode === EPreviewTypes.Session} />
							<EditSectionColorsModal
								open={openStyleModal}
								title={EContentColorOverrideNames.background}
								backgroundImage={workingSession?.layout?.styling_overrides?.background?.image?.src ?? undefined}
								onFinish={handleFinishStyling}
								onClose={handleCancelStyling}
							/>
							<OptionalComponent display={workingSession.session_type === SessionTypesEnum.fireside}>
								<BroadcastOptionsModal
									isOpen={optionsModalOpen}
									onClose={() => setOptionsModalOpen(false)}
								/>
							</OptionalComponent>
						</div>
						{(shouldDisplayFooter && workingEvent?.homepage?.footer) ? (
							<Footer footer={workingEvent?.homepage?.footer}
								template={getTemplateClassName(workingEvent?.template?.name)} />
						) : null}
					</div>
					<PreviewEventChatMain chatTopPosition={chatTopPosition} />
					<CookieBannerStickyWrapper
						style={{
							// position sticky breaks on the editor event page so we must use absolute positioning and calculate the top position
							// luckily we can use the existing scroll event to calculate the top position
							position: 'absolute',
							top: `${(editorMainContainerRef.current?.offsetHeight || 0) + cookieBannerTopPosition - (cookieBannerHeight + 50)}px`,
						}}
					>
						<CookieBanner
							isEditor
							forceShowBanner={!!workingEvent?.settings.gdpr?.enable_cookie_banner}
							containerHeight={height => {
								setCookieBannerHeight(height);
							}}
							forceHeightRecalcutation={cookieBannerTopPosition + editorSizeChangedAsNum}
						/>
					</CookieBannerStickyWrapper>
				</div>
				<ReadOnlyIndicator
					isOn={isReadOnly}
					defaultLanguage={workingEvent.settings.i18n?.base}
				/>

			</div>
		</>
	) : null;
};

export default Editor;
